import { Table, Tooltip } from 'antd'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Title from 'src/branding/Title'
import FadeIn from 'src/components/styled/FadeIn'
import { API } from 'src/utils/interceptor'
import styled from 'styled-components/macro'

const text = value => {
  if (value === null || value === undefined) return ''
  return String(value)
}

const when = value => {
  const date = new Date(value)
  if (!value || Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const rowsOf = value =>
  Array.isArray(value)
    ? value.filter(row => row && typeof row === 'object')
    : []

const shown = value => text(value).trim() || '—'

const columns = [
  {
    title: 'Result',
    dataIndex: 'sentence',
    width: 420,
    render: (sentence, row) => (
      <Tooltip
        title={
          <span>
            {shown(sentence)}
            {text(row.filename) ? (
              <FileTip>{text(row.filename)}</FileTip>
            ) : null}
          </span>
        }
      >
        <Clamp>{shown(sentence)}</Clamp>
      </Tooltip>
    ),
  },
  { title: 'When', dataIndex: 'at', width: 150, render: when },
  {
    title: 'Supplier',
    dataIndex: 'supplierName',
    width: 180,
    render: (value, row) => {
      const fileName = text(value).trim()
      const priceFileTitle = text(row.priceFileTitle).trim()
      const secondTitle = text(row.priceFileSecondTitle).trim()
      const listName = [priceFileTitle, secondTitle].filter(Boolean).join(' ')
      const differs =
        Boolean(fileName) && Boolean(listName) && listName !== fileName
      return (
        <Tooltip
          title={
            differs ? (
              <span>
                {listName}
                <FileTip>Filename: {fileName}</FileTip>
              </span>
            ) : (
              listName || fileName || undefined
            )
          }
        >
          <Clamp>
            {listName || shown(fileName)}
            {differs ? <OneLine>Filename: {fileName}</OneLine> : null}
          </Clamp>
        </Tooltip>
      )
    },
  },
  {
    title: 'Agreement',
    dataIndex: 'storedAgreementNumber',
    width: 140,
    render: (stored, row) => {
      const storedNumber = text(stored).trim()
      const fileNumber = text(row.agreementNumber).trim()
      const differs = Boolean(fileNumber) && fileNumber !== storedNumber
      return (
        <Tooltip
          title={
            differs ? (
              <span>
                {storedNumber || 'No agreement number on the price file'}
                <FileTip>Filename: {fileNumber}</FileTip>
              </span>
            ) : (
              storedNumber || undefined
            )
          }
        >
          <Clamp>{storedNumber || 'Not set'}</Clamp>
        </Tooltip>
      )
    },
  },
  {
    title: 'Venue',
    dataIndex: 'venueName',
    width: 220,
    render: value => (
      <Tooltip title={text(value) || undefined}>
        <OneLine>{shown(value)}</OneLine>
      </Tooltip>
    ),
  },
]

const IronWebCatalogues = () => {
  const username = useSelector(state => state.auth.cognito.username)
  const [summary, setSummary] = useState({
    updated: [],
    waiting: [],
    failed: [],
  })
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setLoadError('')
      try {
        const response = await API.get('api2', 'suppliers/ironweb/catalogues', {
          queryStringParameters: { username },
        })
        if (cancelled) return
        setSummary({
          updated: rowsOf(response?.updated),
          waiting: rowsOf(response?.waiting),
          failed: rowsOf(response?.failed),
        })
      } catch (error) {
        console.error(error)
        if (!cancelled)
          setLoadError('The IronWeb catalogue list could not be loaded.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (username) load()
    return () => {
      cancelled = true
    }
  }, [username])

  return (
    <FadeIn>
      <Title>Catalogues from IronWeb</Title>
      <Intro>
        Updated catalogues are already on the price file. Waiting ones need the
        IronWeb agreement number set before the next run. Failed ones were not
        imported: the file was not a CSV, the price write did not answer, no
        prices changed, the catalogue stopped arriving, or two customers would
        have shared the wrong prices. Hover a result to see the file name.
      </Intro>
      {loadError ? <Intro>{loadError}</Intro> : null}
      <Section title='Updated' rows={summary.updated} loading={loading} />
      <Section title='Waiting' rows={summary.waiting} loading={loading} />
      <Section title='Failed' rows={summary.failed} loading={loading} />
    </FadeIn>
  )
}

const Section = ({ title, rows, loading }) => (
  <Block>
    <h2>
      {title}
      <Count>{rows.length}</Count>
    </h2>
    <Table
      rowKey={row => row.id || `${row.filename}-${row.at}-${row.sentence}`}
      columns={columns}
      dataSource={rows}
      loading={loading}
      pagination={{ pageSize: 8, hideOnSinglePage: true }}
      scroll={{ x: 1100 }}
      locale={{ emptyText: `No ${title.toLowerCase()} catalogues` }}
    />
  </Block>
)

const Intro = styled.p`
  margin: 0 0 1.5rem;
  max-width: 46rem;
`
const Block = styled.section`
  margin-bottom: 2rem;
  h2 {
    margin-bottom: 0.5rem;
  }
`
const Count = styled.span`
  margin-left: 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;
`
const Clamp = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
`
const OneLine = styled.span`
  display: block;
  max-width: 16rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const FileTip = styled.span`
  display: block;
  margin-top: 6px;
  opacity: 0.85;
`

export default IronWebCatalogues
